import { booleanFeatureFlag, Currency, IConfig, timeConsts } from '@eih-netbroker/common';
import { ConfigEnv, dummyConfig } from './EnvConfig';

const env = (process.env || {}) as unknown as {
  REACT_APP_MUI_X_LICENSE_KEY: string;
  REACT_APP_BUILD_VERSION: string;
  REACT_APP_BASE_FONT_SIZE?: string;
};

const cfg = ((window as unknown as { cfg: ConfigEnv }).cfg as ConfigEnv) || dummyConfig;

export type INetBrokerWeb = {
  baseFontSize?: number;
  GridRowHeight: number;
  CustomLayoutDisplayName: {
    MinLength: number;
    MaxLength: number;
  };
  CustomLayoutMaxLimit: number;
  SnackbarTimeout: number;
  MuiLicense: string;
  BETListTradeLimitPerPage: number;
};

export type IConfigWeb = IConfig & {
  NetBrokerWeb: INetBrokerWeb;
  FeatureFlags: {
    isOrderCurrencyExchangeEnabled: boolean;
    isHistoricalPortfolioValueEnabled: boolean;
    isPortfolioNewDesignEnabled: boolean;
    isCashActionEnabled: boolean;
    isMobileAppBannerEnabled: boolean;
    isNewOrderFormEnabled: boolean;
    isUserFeedbackEnabled: boolean;
  };
};

export const Config: IConfigWeb = {
  App: {
    isProduction: !cfg.env,
    buildVersion: env.REACT_APP_BUILD_VERSION,
    EnableAppDebugger: !!cfg.env,
    Domain: cfg.appDomain || 'ersteinv.hu',
    Env: cfg.env ? cfg.env.toUpperCase() : '',
  },
  API: {
    source: 'netbroker-web',
    module: 'netbroker-web',
    ersteMarketUrl: cfg.ersteMarketUrl,
    ersteMarketGoUrl: '',
    eStreamBaseUrl: cfg.eStreamUrl.baseUrl,
    usUrl: {
      baseUrl: cfg.usUrl.base,
      instrument: cfg.usUrl.instrument,
      notification: cfg.usUrl.notification,
      oms: cfg.usUrl.oms,
      partnerData: cfg.usUrl.partnerData,
      session: cfg.usUrl.session,
      userSettings: cfg.usUrl.userSettings,
      priceAlert: cfg.usUrl.priceAlert,
      watchList: cfg.usUrl.watchList,
      positions: cfg.usUrl.positions,
      consent: cfg.usUrl.consent,
      eStream: cfg.usUrl.estream,
      userFeedback: cfg.usUrl.userFeedback,
      account: cfg.usUrl.account,
      currency: cfg.usUrl.currency,
      market: cfg.usUrl.market,
      payment: cfg.usUrl.payment,
    },
    batchedRequestsTimeout: {
      instrument: 500,
      userSettings: 2000,
    },
  },
  SSO: {
    discoveryUrl: cfg.sso.discoveryUrl,
    clientId: cfg.sso.clientId,
    clientSecret: cfg.sso.clientSecret,
    redirectUri: cfg.sso.redirectUri,
  },
  SSE: {
    // EStream SSE url
    eStreamUrl: cfg.eStreamUrl.stream,
    // User stream SSE url
    userStreamUrl: (cfg.usUrl.events.startsWith('https') ? '' : cfg.usUrl.base) + cfg.usUrl.events,
    // when EStream disconnects this timeout will be used to reconnect for the first n times
    FastReconnectTimeout: 3000,
    // when EStream is unreachable for first n times, this timeout will be used to reconnect
    GeneralReconnectTimeout: 15000,
    // EStream maximum reconnect attempts - 200 * GeneralReconnectTimeout
    MaxReconnectAttempt: 200,
    // EStream discovery timeout - how long to wait for instances to be discovered
    DiscoveryTimeout: 1000,
    // EStream throttle timeout - how long to wait before sending a new subscription request
    eStreamSubscribeThrottleTimeout: 300,
  },
  NetBrokerWeb: {
    BETListTradeLimitPerPage: 5000,
    baseFontSize: env.REACT_APP_BASE_FONT_SIZE ? Number(env.REACT_APP_BASE_FONT_SIZE) : undefined,
    GridRowHeight: 36,
    CustomLayoutDisplayName: {
      MinLength: 1,
      MaxLength: 50,
    },
    CustomLayoutMaxLimit: 10,
    SnackbarTimeout: timeConsts.SECONDS_10,
    MuiLicense: env.REACT_APP_MUI_X_LICENSE_KEY,
  },
  NetBroker: {
    BaseCurrency: 'HUF' as Currency,
    PriceAlertCharacterMaxCount: 150,
    Feedback: {
      CharacterMaxCount: 500,
      FilesMaxNumber: 5,
      FilesMaxSize: 10 * 1024 * 1024, // 10 MB
    },
    Watchlist: {
      MaxWatchlist: 10,
      MaxItemsOnWatchlist: 100,
    },
    IndexRow: {
      refreshIntervalInMinutes: 5,
      instruments: [
        '.BUX',
        '.GDAXI',
        '.SX50UL',
        'EURHUF=',
        'EUR=',
        'HUF=',
        'CHFHUF=',
        'GBPHUF=',
        '.STOXXE',
        '.SX18GP',
        '.EDE15BP',
        '.SX21HP',
        '.SX60JP',
        '.TCCNP',
      ],
    },
    Markets: {
      markets: {
        nasdaq: [
          'XNAS',
          'BOSD',
          'ESDP',
          'MELO',
          'NASD',
          'PSXD',
          'XBOS',
          'XBRT',
          'XNCM',
          'XNDQ',
          'XNGS',
          'XNIM',
          'XNMS',
          'XPBT',
          'XPHL',
          'XPHO',
          'XPOR',
          'XPSX',
        ],
        nyse: ['XNYS', 'ALDP', 'ARCD', 'ARCX', 'CISD', 'NYSD', 'XASE', 'XCHI', 'XNLI'],
        frankfurt: ['XFRA', 'FRAA', 'FRAB', 'FRAD', 'FRAS', 'FRAU', 'FRAV', 'FRAW', 'XDBC', 'XDBV', 'XDBX', 'XNEW'],
        xetra: ['XETR', 'XEMA', 'XEMB', 'XEMI', 'XETA', 'XETB', 'XETE', 'XETS'],
        wien: ['XWBO', 'EXAA', 'WBAH', 'WBDM', 'XCEG', 'XVIE'],
        bet: ['XBUD', 'XBU2', 'XBND'],
        beta: ['BETA'],
        xtend: ['XTND'],
        london: ['XLON'],
        otc: ['XXXX'],
      },
    },
    Timeouts: {
      ProductDetailsTimeout: timeConsts.MINUTE,
      GetCurrencyLaterTimeout: timeConsts.SECONDS_30,
      ForceLoadPortfolioTimeout: timeConsts.SECONDS_30,
      ForceLogout: {
        DefaultTimeout: timeConsts.MINUTE_15,
        WarningTimeout: timeConsts.MINUTE,
      },
      /**
       * Timeouts for chart refresh. This is used to determine how often the chart should be refreshed using OHLC data.
       */
      RealtimeChartRefresh: {
        IntraDay: timeConsts.SECONDS_30,
        InterDay: timeConsts.MINUTE_15,
      },
    },
    Headers: {
      Src: 'netbrokernew',
      SrcModule: 'netbrokernew',
      ChannelWeb: 'web',
    },
    CurrencyExchange: {
      HaveNightPeriods: ['EUR', 'USD', 'HUF'],
    },
  },
  Chart: {
    ChartIQVersion: '10.4.0',
    /**
     * Maximum number of retries for chart query when there is no OHLC retrieved for the given period.
     * Next retry will try to load data from a day before.
     */
    MaxChartQueryRetry: 6,
  },
  Links: {
    Registration: cfg.links?.registration || 'https://www.erstebank.hu',
    Contact: cfg.links?.contact || 'https://www.erstebank.hu',
    Legal: cfg.links?.legal || 'https://www.erstebank.hu',
    Technical: cfg.links?.technical || 'https://www.erstebank.hu',
    CurrencyExchange:
      cfg.links?.currencyExchange ||
      'https://www.erstemarket.hu/gyakori-kerdesek/kereskedes/devizavaltas-es-devizaugyletek/milyen-limitek-mellett-valthatok-devizat',
    OldNetbroker: cfg.links?.oldNetbroker || 'https://netbroker.erstebroker.hu/',
  },
  FeatureFlags: {
    isOrderCurrencyExchangeEnabled: booleanFeatureFlag(cfg.featureFlags.isOrderCurrencyExchangeEnabled),
    isHistoricalPortfolioValueEnabled: booleanFeatureFlag(cfg.featureFlags.isHistoricalPortfolioValueEnabled),
    isPortfolioNewDesignEnabled: booleanFeatureFlag(cfg.featureFlags.isPortfolioNewDesignEnabled),
    isCashActionEnabled: booleanFeatureFlag(cfg.featureFlags.isCashActionEnabled),
    isMobileAppBannerEnabled: booleanFeatureFlag(cfg.featureFlags.isMobileAppBannerEnabled),
    isNewOrderFormEnabled: booleanFeatureFlag(cfg.featureFlags.isNewOrderFormEnabled),
    isUserFeedbackEnabled: booleanFeatureFlag(cfg.featureFlags.isUserFeedbackEnabled),
  },
};
